import React, { forwardRef, useId, useRef } from "react";
import Icon from "./Icon";
import "./Input.scss";

// Fontaine M17 — Input. Visual contract: Style Guide V2 `input-*` (44px control, surface, hairline border,
// lilac focus ring, danger error ring). Prefix/suffix slots (currency, unit, icon), clear button.

const SIZES = ["md", "lg"];
// legacy sizes from the pill-search era
const LEGACY_SIZES = { sm: "md", xl: "lg", "2xl": "lg" };

/**
 * @param {"md"|"lg"} [size="lg"]                 40px · 44px (V2 default)
 * @param {ReactNode} [prefix] / [suffix]         adornments (text or <Icon />) — legacy aliases leading / trailing
 * @param {boolean} [clearable]                   shows an × when there is a value; calls onClear (or onChange with "")
 * @param {boolean} [invalid]                     error styling + aria-invalid (the message belongs to Field)
 * @param {boolean} [round]                       pill radius (search bars) — legacy alias `pill`
 * @param {boolean} [fullWidth]
 */
const Input = forwardRef(function Input(
  {
    size = "lg",
    prefix,
    suffix,
    leading, // legacy
    trailing, // legacy
    pill, // legacy
    round = false,
    clearable = false,
    onClear,
    invalid = false,
    fullWidth = false,
    disabled = false,
    className = "",
    inputClassName = "",
    type = "text",
    value,
    onChange,
    id,
    ...rest
  },
  ref
) {
  const autoId = useId();
  const inputId = id || `ft-input-${autoId}`;
  const inner = useRef(null);
  const setRefs = (el) => {
    inner.current = el;
    if (typeof ref === "function") ref(el);
    else if (ref) ref.current = el;
  };
  const resolvedSize = SIZES.includes(size) ? size : LEGACY_SIZES[size] || "lg";
  const pre = prefix ?? leading;
  const suf = suffix ?? trailing;
  const hasValue = value != null && value !== "";
  const showClear = clearable && hasValue && !disabled;
  // A search field's × is named "Clear search", not a bare "Clear" — it is the name the board's
  // own contract asserts (tests/views/KanbanRequests.test.jsx), and the more useful one out of
  // context, where "Clear" alone says nothing about what it clears.
  const clearLabel = type === "search" ? "Clear search" : "Clear";

  const clear = () => {
    if (onClear) onClear();
    else if (onChange) onChange({ target: { value: "" }, currentTarget: { value: "" } });
    inner.current?.focus();
  };

  const shell = [
    "ft-input",
    `ft-input--${resolvedSize}`,
    round || pill ? "ft-input--round" : "",
    fullWidth ? "ft-input--full" : "",
    invalid ? "is-invalid" : "",
    disabled ? "is-disabled" : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={shell}>
      {pre ? <span className="ft-input__affix ft-input__affix--prefix">{pre}</span> : null}
      <input
        ref={setRefs}
        id={inputId}
        type={type}
        className={["ft-input__control", inputClassName].filter(Boolean).join(" ")}
        value={value}
        onChange={onChange}
        disabled={disabled}
        aria-invalid={invalid || undefined}
        {...rest}
      />
      {showClear ? (
        <button type="button" className="ft-input__clear" aria-label={clearLabel} onClick={clear} tabIndex={-1}>
          <Icon name="X" size="xs" />
        </button>
      ) : null}
      {suf ? <span className="ft-input__affix ft-input__affix--suffix">{suf}</span> : null}
    </div>
  );
});

export default Input;
